import { useDispatch } from 'react-redux';
import { Button, DialogTitle, DialogActions, DialogContent, type DialogProps } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import useSettings from '../hooks/useSettings';
import Form from '../Form';

interface ConfirmCancelDialogProps {
	/** The dialog title component */
	title: React.ReactNode;
	/** The dialog content component */
	content?: React.ReactNode;
	/** Function called when the 'confirm' button is pressed */
	onConfirm?: VoidFunction;
	/** Function called when the 'cancel' button is pressed */
	onCancel?: VoidFunction;
	/** Function called to close the dialog */
	onClose?: VoidFunction;
	/** Props used on the dialog */
	dialogProps?: Omit<DialogProps, 'open'>;
	/** Optional alternative locale */
	locale?: {
		cancelButton?: string;
		confirmButton?: string;
	};
}

export const showConfirmCancelDialog = ({ dialogProps = {}, ...props }: ConfirmCancelDialogProps) =>
	openDialog({
		children: <ConfirmCancelDialog {...props} />,
		...dialogProps,
		maxWidth: dialogProps?.maxWidth || 'xs',
		onClose: dialogProps?.onClose || (() => {})
	});

export default function ConfirmCancelDialog(props: ConfirmCancelDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const close = () => {
		if (props.onClose) props.onClose();
		else dispatch(closeDialog());
	};

	const cancel = () => {
		close();
		props.onCancel?.();
	};

	const confirm = () => {
		close();
		props.onConfirm?.();
	};

	return (
		<Form id="confirmCancelDialog" onSubmit={confirm}>
			<DialogTitle>{props.title}</DialogTitle>
			{props.content && <DialogContent>{props.content}</DialogContent>}
			<DialogActions>
				<Button onClick={cancel}>{t(props.locale?.cancelButton || 'DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="confirmCancelDialog">
					{t(props.locale?.confirmButton || 'DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</Form>
	);
}
